import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import { Checkbox } from '@/shared/ui/checkbox';
import { Badge } from '@/shared/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { Loader2, Paperclip, X, ChevronDown, ChevronUp } from 'lucide-react';
import CustomCombobox from '@/shared/ui/custom/combobox';
import { AddNewTradeDialog } from '@/shared/forms';
import {
  useObservationMutations,
  useObservationTypes,
  useCreateObservationType,
} from '@/features/observations/hooks';
import { useTrades, useCreateTrade, useProjectUsers } from '@/features/punch-lists/hooks';
import type {
  CreateObservationData,
  ObservationPriority,
  UserMinimal,
} from '@/features/observations/types';
import { ViewPosition } from '../types/types';
import { useCreateHotspot } from '../hooks/useHotspotData';
import { AttachmentPhotoGallery, type AttachmentPhoto } from '@/shared/components/AttachmentPhotoGallery';
import type { PendingPhotoUpload } from './pendingPhotoUpload';

interface ObservationHotspotFormProps {
  photoId: number;
  projectId: number | undefined;
  viewPosition: ViewPosition;
  hotspotId?: number;
  initialFiles?: File[];
  initialPreviewEntries?: Array<{
    id: string;
    file: File;
    previewUrl: string;
    isExternalPreview?: boolean;
  }>;
  pendingUploads?: PendingPhotoUpload[];
  onRemovePendingUpload?: (id: string) => void;
  onSuccess: () => void;
  onCancel: () => void;
}

const PRIORITY_OPTIONS: { value: ObservationPriority; label: string }[] = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
];

type FormState = Omit<CreateObservationData, 'project' | 'hotspot'>;

const INITIAL_FORM: FormState = {
  title: '',
  priority: 'MEDIUM',
  status: 'OPEN',
  description: '',
  is_private: false,
  due_date: '',
  spec_section: '',
  assignee: null,
  observation_type: null,
  trade: null,
  location: null,
  distribution_list: [],
  hazard: '',
  contributing_behavior: '',
  contributing_condition: '',
};

const userDisplayName = (u: UserMinimal) =>
  `${u.first_name} ${u.last_name}`.trim() || u.email;

export const ObservationHotspotForm: React.FC<ObservationHotspotFormProps> = ({
  photoId,
  projectId,
  viewPosition,
  hotspotId: existingHotspotId,
  initialFiles = [],
  initialPreviewEntries = [],
  pendingUploads = [],
  onRemovePendingUpload,
  onSuccess,
  onCancel,
}) => {
  const { createItem, uploadAttachment, linkAttachmentPhoto } = useObservationMutations(projectId);
  const { data: observationTypes } = useObservationTypes();
  const { data: trades } = useTrades();
  const { data: projectUsers } = useProjectUsers(projectId);
  const createObservationType = useCreateObservationType();
  const createTrade = useCreateTrade();
  const createHotspotMutation = useCreateHotspot(
    projectId == null ? undefined : String(projectId),
  );

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [pendingFiles, setPendingFiles] = useState<Array<{ id: string; file: File; previewUrl: string; isExternalPreview?: boolean }>>(() => {
    if (pendingUploads.length > 0) return [];
    if (initialPreviewEntries.length > 0) {
      return initialPreviewEntries.map((entry) => ({ ...entry, isExternalPreview: true }));
    }
    return initialFiles.map((file, index) => ({
      id: `${file.name}-${file.lastModified}-${Date.now()}-${index}`,
      file,
      previewUrl: URL.createObjectURL(file),
      isExternalPreview: false,
    }));
  });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showSafety, setShowSafety] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingFilesRef = useRef<Array<{ id: string; file: File; previewUrl: string; isExternalPreview?: boolean }>>([]);

  useEffect(() => {
    pendingFilesRef.current = pendingFiles;
  }, [pendingFiles]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const setNullableUser = (key: 'assignee', raw: string) =>
    setForm((prev) => ({ ...prev, [key]: raw === 'none' ? null : Number(raw) }));

  const toggleDistribution = (userId: number) => {
    setForm((prev) => {
      const current = prev.distribution_list as number[];
      return {
        ...prev,
        distribution_list: current.includes(userId)
          ? current.filter((id) => id !== userId)
          : [...current, userId],
      };
    });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length) {
      setPendingFiles((prev) => [
        ...prev,
        ...files.map((file, index) => ({
          id: `${file.name}-${file.lastModified}-${Date.now()}-${index}`,
          file,
          previewUrl: URL.createObjectURL(file),
          isExternalPreview: false,
        })),
      ]);
    }
    e.target.value = '';
  };

  useEffect(() => () => {
    pendingFilesRef.current.forEach((entry) => {
      if (!entry.isExternalPreview) URL.revokeObjectURL(entry.previewUrl);
    });
  }, []);

  const attachmentPreviews = useMemo<AttachmentPhoto[]>(
    () => [
      ...pendingUploads.map((entry) => ({
        id: entry.id,
        file: entry.file.name,
        file_url: entry.previewUrl,
      })),
      ...pendingFiles.map((entry) => ({
        id: entry.id,
        file: entry.file.name,
        file_url: entry.previewUrl,
      })),
    ],
    [pendingFiles, pendingUploads],
  );

  const removeFileById = (attachmentId: string) => {
    if (pendingUploads.some((entry) => entry.id === attachmentId)) {
      onRemovePendingUpload?.(attachmentId);
      return;
    }
    setPendingFiles((prev) => {
      const target = prev.find((entry) => entry.id === attachmentId);
      if (target && !target.isExternalPreview) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((entry) => entry.id !== attachmentId);
    });
  };

  const handleAddNewType = async (name: string) => {
    const newType = await createObservationType.mutateAsync(name);
    set('observation_type', newType.id);
  };

  const handleAddNewTrade = async (name: string) => {
    const newTrade = await createTrade.mutateAsync(name);
    set('trade', newTrade.id);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('Title is required.'); return; }
    if (!projectId) { setError('No project context available.'); return; }
    setError(null);
    setSubmitting(true);

    try {
      let hotspotId = existingHotspotId;
      if (!hotspotId) {
        const hotspotResponse = await createHotspotMutation.mutateAsync({
          photoId,
          position: {
            pitch: viewPosition.pitch,
            yaw: viewPosition.yaw,
            hfov: viewPosition.hfov,
          },
        });
        hotspotId = hotspotResponse.id;
      }

      const payload: CreateObservationData = {
        ...form,
        project: projectId,
        hotspot: hotspotId,
        due_date: form.due_date || null,
        assignee: form.assignee || null,
        observation_type: form.observation_type || null,
        trade: form.trade || null,
        location: form.location || null,
      };

      const newItem = await createItem.mutateAsync(payload);

      const settled = await Promise.allSettled(
        pendingUploads.map((entry) => entry.uploadPromise),
      );
      const linkOps: Promise<unknown>[] = [];
      settled.forEach((result, idx) => {
        const entry = pendingUploads[idx];
        if (result.status === 'fulfilled') {
          linkOps.push(
            linkAttachmentPhoto.mutateAsync({
              observationId: newItem.id,
              photoId: result.value.photoId,
            }),
          );
        } else {
          linkOps.push(
            uploadAttachment.mutateAsync({ observationId: newItem.id, file: entry.file }),
          );
        }
      });
      pendingFiles.forEach((attachment) => {
        linkOps.push(
          uploadAttachment.mutateAsync({ observationId: newItem.id, file: attachment.file }),
        );
      });
      await Promise.all(linkOps);

      setForm(INITIAL_FORM);
      pendingFiles.forEach((entry) => {
        if (!entry.isExternalPreview) URL.revokeObjectURL(entry.previewUrl);
      });
      setPendingFiles([]);
      onSuccess();
    } catch {
      setError('Failed to create observation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const distList = (form.distribution_list as number[]) ?? [];

  return (
    <form onSubmit={handleSubmit} className="space-y-4 pt-1">
      {/* Title */}
      <div className="space-y-1">
        <Label htmlFor="obs-hs-title">Title <span className="text-destructive">*</span></Label>
        <Input id="obs-hs-title" placeholder="Describe the observation" value={form.title} onChange={(e) => set('title', e.target.value)} autoFocus />
      </div>

      {/* Priority + Type */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label>Priority</Label>
          <Select value={form.priority} onValueChange={(v) => set('priority', v as ObservationPriority)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {PRIORITY_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Type</Label>
          <CustomCombobox
            options={(observationTypes ?? []).map((t) => ({ value: t.id, label: t.name }))}
            value={form.observation_type ?? ''}
            onChange={(v) => set('observation_type', v || null)}
            placeholder="Select type"
            searchPlaceholder="Search types…"
            addNewLabel="Add New Type"
            emptyMessage="No types found."
            onAddNew={handleAddNewType}
          />
        </div>
      </div>

      {/* Assignee */}
      <div className="space-y-1">
        <Label>Assignee</Label>
        <Select value={form.assignee != null ? String(form.assignee) : 'none'} onValueChange={(v) => setNullableUser('assignee', v)}>
          <SelectTrigger><SelectValue placeholder="Select assignee" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            {(projectUsers ?? []).map((u) => (<SelectItem key={u.id} value={String(u.id)}>{userDisplayName(u)}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      {/* Due Date + Trade */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <Label htmlFor="obs-hs-due">Due Date</Label>
          <Input id="obs-hs-due" type="datetime-local" value={form.due_date ?? ''} onChange={(e) => {
            const val = e.target.value;
            if (!val) { set('due_date', val); return; }
            if (!form.due_date) { set('due_date', `${val.slice(0, 10)}T23:59`); } else { set('due_date', val); }
          }} />
        </div>
        <div className="space-y-1">
          <Label>Trade</Label>
          <CustomCombobox
            options={(trades ?? []).map((t) => ({ value: t.id, label: t.name }))}
            value={form.trade ?? ''}
            onChange={(v) => set('trade', v || null)}
            placeholder="Select trade"
            searchPlaceholder="Search trades…"
            addNewLabel="Add New Trade"
            emptyMessage="No trades found."
            onAddNew={handleAddNewTrade}
            AddNewDialog={AddNewTradeDialog}
          />
        </div>
      </div>

      {/* Description */}
      <div className="space-y-1">
        <Label htmlFor="obs-hs-desc">Description</Label>
        <Textarea id="obs-hs-desc" placeholder="Describe what was observed" rows={3} value={form.description} onChange={(e) => set('description', e.target.value)} />
      </div>

      {/* Safety / Quality (collapsible) */}
      <div className="border rounded-lg">
        <button type="button" className="w-full flex items-center justify-between p-3 text-sm font-medium hover:bg-accent transition-colors rounded-lg" onClick={() => setShowSafety(!showSafety)}>
          Safety / Quality Details
          {showSafety ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        {showSafety && (
          <div className="px-3 pb-3 space-y-3">
            <div className="space-y-1">
              <Label htmlFor="obs-hs-hazard">Hazard</Label>
              <Textarea id="obs-hs-hazard" placeholder="Describe the hazard" rows={2} value={form.hazard} onChange={(e) => set('hazard', e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="obs-hs-behavior">Contributing Behavior</Label>
              <Textarea id="obs-hs-behavior" rows={2} value={form.contributing_behavior} onChange={(e) => set('contributing_behavior', e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="obs-hs-condition">Contributing Condition</Label>
              <Textarea id="obs-hs-condition" rows={2} value={form.contributing_condition} onChange={(e) => set('contributing_condition', e.target.value)} />
            </div>
          </div>
        )}
      </div>

      {/* Distribution List */}
      <div className="space-y-2">
        <Label>Distribution List</Label>
        <div className="border rounded-md p-3 max-h-32 overflow-y-auto space-y-1">
          {(projectUsers ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No users available</p>
          ) : (projectUsers ?? []).map((u) => (
            <div key={u.id} className="flex items-center gap-2">
              <Checkbox id={`obs-hs-dist-${u.id}`} checked={distList.includes(u.id)} onCheckedChange={() => toggleDistribution(u.id)} />
              <label htmlFor={`obs-hs-dist-${u.id}`} className="text-sm cursor-pointer">{userDisplayName(u)}</label>
            </div>
          ))}
        </div>
        {distList.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {distList.map((id) => {
              const u = (projectUsers ?? []).find((u) => u.id === id);
              return u ? (<Badge key={id} variant="secondary" className="gap-1">{userDisplayName(u)}<X className="h-3 w-3 cursor-pointer" onClick={() => toggleDistribution(id)} /></Badge>) : null;
            })}
          </div>
        )}
      </div>

      {/* Attachments */}
      <div className="space-y-2">
        <Label>Attachments</Label>
        <div>
          <Button type="button" variant="outline" size="sm" className="gap-1" onClick={() => fileInputRef.current?.click()}>
            <Paperclip className="h-4 w-4" /> Attach Files
          </Button>
          <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileSelect} />
        </div>
        {attachmentPreviews.length > 0 && (
          <AttachmentPhotoGallery attachments={attachmentPreviews} maxVisible={6} onDelete={removeFileById} />
        )}
      </div>

      {/* Private */}
      <div className="flex items-center gap-2">
        <Checkbox id="obs-hs-private" checked={form.is_private} onCheckedChange={(checked) => set('is_private', checked === true)} />
        <Label htmlFor="obs-hs-private" className="cursor-pointer">Private</Label>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={submitting}>
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Create Observation
        </Button>
      </div>
    </form>
  );
};
